import {
  createTableLinkWithOpenNewTabColumn,
  createTableLinkWithOpenNewTabElement,
} from '@common/tableColumn.services';
import {
  Column,
  createTableCell,
  createTableColumn,
  Grid,
  Heading,
  HeadingType,
  ModalBodyExtras,
  PaginatedTable,
  useModal,
} from '@gcl/react-core';
import { useNavigate } from '@routes/routes.hooks';
import { routes } from '@routes/routes.models';
import * as React from 'react';
import { useProjectId } from '@config/config.hooks';
import { useFetchDrawingHistories } from './projectDrawingRegister.queries';
import { IItemConstructionHistory, IItemDesignHistory } from './projectDrawingRegister.models';

export interface IUseDocumentRegisterHistoryModalProps {
  projectMasterScheduleId?: number;
  transmittalItemId?: number;
}

export const useDocumentRegisterHistoryModal = ({
  projectMasterScheduleId,
  transmittalItemId,
}: IUseDocumentRegisterHistoryModalProps) => {
  const { openPortal, closePortal, renderModal } = useModal();
  const { navigate, generateRoutePath } = useNavigate();
  const projectId = useProjectId();

  const { data: dataDrawingHistories } = useFetchDrawingHistories({
    projectId,
    projectMasterScheduleId: projectMasterScheduleId ?? -1,
    transmittalItemId: transmittalItemId ?? -1,
  });

  const designHistoryTableColumns = React.useMemo(
    () => [
      createTableLinkWithOpenNewTabColumn<IItemDesignHistory>(
        'transmittalNo',
        'Transmittal No.',
        ({ item }) => generateRoutePath(routes.transmittals.item.path, { id: item.transmittalId?.toString() }),
        ({ value, item, index, cellExtras }, link) => link,
      ),
      createTableColumn({
        Header: 'Revision',
        accessor: 'revision',
      } as Column),
      createTableColumn({
        Header: 'T1 Ref. No.',
        accessor: 't1RefNo',
      } as Column),
    ],
    [],
  );

  const constructionHistoryTableColumns = React.useMemo(
    () => [
      createTableColumn({
        Header: 'Revision',
        accessor: 'revision',
      } as Column),
      createTableColumn({
        Header: 'T1 Ref. No.',
        accessor: 't1RefNo',
      } as Column),
      createTableColumn({
        accessor: 'designModifications',
        Header: 'T3 Modification Request',
        disableResizing: true,
        disableSortBy: true,
        Cell: createTableCell<string, IItemConstructionHistory>((value, item, index, cellExtras) => {
          return item.designModifications?.map((t3) => {
            const linkUrl = generateRoutePath(routes.t3.view.path, { id: t3.id });
            return (
              <div key={t3.designChangeNo}>
                {createTableLinkWithOpenNewTabElement({ linkUrl, linkDisplay: t3.designChangeNo ?? '' })}
              </div>
            );
          });
        }),
      } as Column),
    ],
    [],
  );

  const openDocumentRegisterHistoryModal = (e: React.MouseEvent) => {
    openPortal(e);
  };

  const renderDocumentRegisterHistoryModal = () => {
    return renderModal({
      large: true,
      title: `Document Revision History`,
      onRenderBody: (bodyExtras?: ModalBodyExtras) => {
        return (
          <Grid container spacing={2}>
            <Grid item sm={12} md={6}>
              <Heading as={HeadingType.h1} styledAs={HeadingType.h6} className="gcl-pb-xs">
                Design History
              </Heading>
              {!!dataDrawingHistories?.designHistories?.length && (
                <PaginatedTable
                  columns={designHistoryTableColumns}
                  items={dataDrawingHistories?.designHistories ?? []}
                  disablePagination
                  allowSelectRow={false}
                />
              )}
              {(!dataDrawingHistories?.designHistories || !dataDrawingHistories?.designHistories.length) && (
                <div className="gcl-gems-document-register-history-modal-no-history">No history found</div>
              )}
            </Grid>
            <Grid item sm={12} md={6}>
              <Heading as={HeadingType.h1} styledAs={HeadingType.h6} className="gcl-pb-xs">
                Construction History
              </Heading>
              {!!dataDrawingHistories?.constructionHistories?.length && (
                <PaginatedTable
                  columns={constructionHistoryTableColumns}
                  items={dataDrawingHistories?.constructionHistories ?? []}
                  disablePagination
                  allowSelectRow={false}
                />
              )}
              {(!dataDrawingHistories?.constructionHistories ||
                !dataDrawingHistories?.constructionHistories.length) && (
                <div className="gcl-gems-document-register-history-modal-no-history">No history found</div>
              )}
            </Grid>
          </Grid>
        );
      },
    });
  };
  return {
    openPortal: openDocumentRegisterHistoryModal,
    closePortal,
    renderModal: renderDocumentRegisterHistoryModal,
  };
};
